import { RefreshButton } from '@euler/app/components/RefreshButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import {
  ListMask,
  useScrollableMask,
} from '@euler/app/components/list/ListMask';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { Colors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { SegmentControl } from '@euler/components/SegmentControl';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  isMobileBrowser,
  useIsDesktopLayout,
} from '@euler/functions/responsiveness';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { usePersistFn } from '@euler/utils/hooks';
import { DrawerActions, useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { memo, useCallback } from 'react';
import {
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';

type Props = {
  segments: string[];
  tags: string[] | undefined;
  segmentIndex: number;
  showSidebarTrigger?: boolean;
  activeTag?: string;
  defaultTitle: string;
  onTagChange?: (tag: string | undefined) => void;
  onRefresh?: () => void;
  onSearchPress?: () => void;
  onSegmentIndexChange: (index: number) => void;
};

export const DashboardHeader = memo(
  ({
    segments,
    tags,
    showSidebarTrigger,
    segmentIndex,
    activeTag,
    defaultTitle,
    onTagChange,
    onSearchPress,
    onSegmentIndexChange,
    onRefresh,
  }: Props) => {
    const { rpx } = useResponsiveContext();
    const isDesktop = useIsDesktopLayout();

    const { maxSafeAreaTop: top, maxNavBarHeight: navBarHeight } =
      useSystemMetrics();

    const navigation = useNavigation();

    const onDrawerPress = useCallback(() => {
      navigation.dispatch(DrawerActions.openDrawer());
    }, [navigation]);

    return (
      <View
        css={`
          height: ${navBarHeight}px;
          ${isDesktop
            ? `background-color: #fff; border-bottom-width: 1px; border-bottom-color: #e5e7eb;`
            : ''}
        `}
      >
        {/** shade */}
        {!isDesktop && (
          <LinearGradient
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 1 }}
            colors={['#006be4', '#5ba0e0']}
            locations={[0.335, 1]}
            style={StyleSheet.absoluteFill}
            css={`
              flex: 1;
            `}
          />
        )}
        {isDesktop && !tags?.length ? (
          <View
            css={`
              position: absolute;
              top: 0;
              bottom: 0;
              left: ${rpx(24)}px;
              padding-top: ${top}px;
              flex-direction: row;
              align-items: center;
            `}
          >
            <Typography.Text
              bold
              size={rpx(20)}
              color="#0f172a"
              letterSpacing={rpx(0.5)}
            >
              {defaultTitle}
            </Typography.Text>
          </View>
        ) : null}
        {tags?.length ? (
          <NavBar
            segments={segments}
            tags={tags}
            segmentIndex={segmentIndex}
            activeTag={activeTag}
            dark={isDesktop}
            onSegmentIndexChange={onSegmentIndexChange}
            onTagChange={onTagChange}
            css={`
              padding-top: ${top}px;
            `}
          />
        ) : (
          <Center
            style={StyleSheet.absoluteFill}
            css={`
              padding-top: ${top}px;
            `}
          >
            {segments.length > 1 ? (
              <SegmentPicker
                size={'large'}
                segments={segments}
                segmentIndex={segmentIndex}
                onSegmentIndexChange={onSegmentIndexChange}
                dark={isDesktop}
              />
            ) : !isDesktop ? (
              <Typography.Text color="white" bold size={rpx(16)}>
                {defaultTitle}
              </Typography.Text>
            ) : null}
          </Center>
        )}

        {showSidebarTrigger && !isDesktop && (
          <TouchableOpacity
            css={`
              flex-direction: row;
              justify-content: flex-start;
              align-items: center;
              flex-grow: 0;
              position: absolute;
              top: 0;
              bottom: 0;
              padding-top: ${top}px;
              left: ${rpx(15)}px;
            `}
            hitSlop={PredefinedHitSlops.large}
            onPress={onDrawerPress}
          >
            {/* No unread badge here — the tab bar's 消息中心 item carries it. */}
            <Icon name="bars" color="white" size={rpx(20)} />
          </TouchableOpacity>
        )}

        <HStack
          justifyContent="flex-end"
          css={`
            flex-grow: 0;
            position: absolute;
            top: 0;
            bottom: 0;
            padding-top: ${top}px;
            right: ${rpx(15)}px;
          `}
        >
          {Platform.OS === 'web' && (
            <RefreshButton
              css={`
                margin-right: ${rpx(15)}px;
              `}
              iconProps={{
                color: isDesktop ? '#475569' : '#fff',
                size: rpx(20),
              }}
              onPress={onRefresh}
            />
          )}
          <TouchableOpacity
            css={`
              justify-content: center;
              align-items: center;
            `}
            hitSlop={PredefinedHitSlops.large}
            onPress={onSearchPress}
          >
            <Icon
              name="search"
              color={isDesktop ? '#475569' : 'white'}
              size={rpx(20)}
            />
          </TouchableOpacity>
        </HStack>
      </View>
    );
  },
);

const NavBar = memo(
  ({
    segments,
    tags,
    style,
    activeTag,
    segmentIndex,
    dark,
    onTagChange,
    onSegmentIndexChange,
  }: {
    segments: string[];
    tags: string[];
    activeTag?: string;
    segmentIndex: number;
    style?: StyleProp<ViewStyle>;
    /** Use light/desktop chrome (dark text). */
    dark?: boolean;
    onTagChange?: (tag: string | undefined) => void;
    onSegmentIndexChange: (index: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      showLeadingMask,
      showTrailingMask,
      onLayout,
      onContentSizeChange,
      onScroll,
    } = useScrollableMask({
      orientation: 'horizontal',
    });

    const onTagPress = usePersistFn((tag: string | undefined) => {
      if (tag != activeTag) {
        onTagChange?.(tag);
      }
    });

    const tagList: (string | undefined)[] = [undefined, ...tags];

    return (
      <StackLayout
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={rpx(20)}
        style={[
          StyleSheet.absoluteFill,
          {
            // Desktop has no hamburger on the left; align the first tag
            // with the page gutter (24px). Mobile keeps the gap that
            // accounts for the hamburger button.
            paddingLeft: rpx(dark ? 24 : 45),
            paddingRight: rpx(Platform.OS === 'web' ? 90 : 45),
          },
          style,
        ]}
      >
        <ListMask
          leadingMask={showLeadingMask}
          trailingMask={showTrailingMask}
          style={{ flex: 1 }}
        >
          <ScrollView
            horizontal={true}
            showsHorizontalScrollIndicator={false}
            scrollEventThrottle={1}
            onScroll={onScroll}
            onLayout={onLayout}
            onContentSizeChange={onContentSizeChange}
            contentContainerStyle={{
              flexGrow: 1,
              alignItems: 'center',
            }}
          >
            {tagList.map(tag => (
              <TouchableOpacity
                key={tag ?? ''}
                onPress={() => onTagPress(tag)}
                css={`
                  margin-right: ${rpx(10)}px;
                `}
              >
                <Typography.Label
                  family={tag == activeTag ? 'bold' : 'medium'}
                  size={rpx(tag == activeTag ? 21 : 16)}
                  lineHeight={rpx(tag == activeTag ? 32 : 32)}
                  color={dark ? '#0f172a' : '#fff'}
                  opacity={tag == activeTag ? 1 : dark ? 0.5 : 0.5}
                >
                  {tag || '全部'}
                </Typography.Label>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </ListMask>
        <SegmentPicker
          segments={segments}
          size={Platform.OS === 'web' && !isMobileBrowser() ? 'large' : 'small'}
          maxWidth={rpx(120)}
          segmentIndex={segmentIndex}
          dark={dark}
          onSegmentIndexChange={onSegmentIndexChange}
        />
      </StackLayout>
    );
  },
);

const SegmentPicker = memo(
  ({
    segments,
    segmentIndex,
    style,
    size = 'large',
    maxWidth,
    dark,
    onSegmentIndexChange,
  }: {
    segments: string[];
    segmentIndex: number;
    style?: StyleProp<ViewStyle>;
    size?: 'small' | 'large';
    maxWidth?: number;
    /** Render against a light/desktop background (dark text, gray border). */
    dark?: boolean;
    onSegmentIndexChange: (index: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { width: vw } = useWindowDimensions();
    const borderColor = dark ? '#e5e7eb' : Colors.White4_5;
    const inactiveTextColor = dark ? '#475569' : Colors.White;
    const activeTextColor = dark ? '#0f172a' : Colors.White;
    return (
      <SegmentControl
        segments={segments}
        selectedIndex={segmentIndex}
        onSelectedIndexChange={onSegmentIndexChange}
        css={`
          flex-shrink: 0;
          width: ${size === 'small'
            ? rpx(60)
            : maxWidth ?? Math.min(vw * 0.55, 350)}px;
          background-color: ${dark ? '#f1f5f9' : 'transparent'};
          border-width: 1px;
          border-color: ${borderColor};
          height: ${rpx(size == 'small' ? 24 : 32)}px;
          border-radius: ${rpx(10)}px;
        `}
        sliderStyle={{
          // note(eric): this only works on native but does not work for web
          borderRadius: rpx(8),

          // on react-native-web 0.19+, overriding style should be exhaustive
          // for styled-components which are styled with `css` prop.
          borderTopLeftRadius: rpx(8),
          borderTopRightRadius: rpx(8),
          borderBottomLeftRadius: rpx(8),
          borderBottomRightRadius: rpx(8),
          ...(dark ? { backgroundColor: '#fff' } : null),
        }}
        textStyle={
          size === 'small'
            ? {
                color: inactiveTextColor,
                fontSize: rpx(8),
                fontFamily: FontFamily.NotoSans.Medium,
              }
            : { color: inactiveTextColor }
        }
        activeTextStyle={
          size === 'small'
            ? {
                // Don't override `color` on mobile small — the underlying
                // SegmentControl supplies the right contrast against the
                // slider pill (mobile blue chrome). Dark mode is the desktop
                // case and needs an explicit dark color on the white pill.
                fontSize: rpx(8),
                fontFamily: FontFamily.NotoSans.Bold,
                ...(dark ? { color: activeTextColor } : null),
              }
            : dark
            ? { color: activeTextColor, fontFamily: FontFamily.NotoSans.Bold }
            : undefined
        }
        style={style}
      />
    );
  },
);
